<script>

    import { tick } from "svelte";
    import { best_audio_offer, BestAudio } from "../../BestAudio.js";

    export let open = false;

    const SETTLE_MS = 1500; // -- nothing signals the headset leaving call mode; give it time before closing

    let dlg;
    let ok_btn;
    let cancel_btn;
    let shown = null;       // the offer on screen, frozen while a switch is in flight
    let pending = false;
    let failed = null;      // {reason, label, deviceId} of the last attempt
    let down_on_backdrop = false;

    // -- follow the live offer while idle; with none left close, unless a failure still needs reading
    $: if (open && !pending){
        if ($best_audio_offer) shown = $best_audio_offer;
        else if (!failed) open = false;
    }

    $: if (dlg){
        if (open && !dlg.open){
            dlg.showModal();
            focusAction();
        }
        else if (!open && dlg.open)
            dlg.close();
    }

    $: retry = !!failed && failed.deviceId === shown?.target?.deviceId;

    function focusAction(){
        tick().then(() => (ok_btn ?? cancel_btn)?.focus());
    }

    async function ok(){
        if (pending || !shown) return;
        const tried = shown;
        pending = true;
        failed = null;
        const result = await BestAudio.apply(tried).catch(e => ({ ok: false, reason: e?.name ?? "error" }));
        if (result.ok){
            await new Promise(r => setTimeout(r, SETTLE_MS)); // -- "Switching…" stays up meanwhile
            pending = false;
            open = false;
            return;
        }
        pending = false;
        failed = { reason: result.reason, label: tried.to_label, deviceId: tried.target.deviceId };
        focusAction(); // -- disabling OK while pending dropped focus to <body>
    }

    function cancel(){
        if (pending) return;
        if (!failed) BestAudio.logDeclined(shown);
        open = false;
    }

    // -- Escape: route through cancel so it logs
    function onCancel(e){
        e.preventDefault();
        cancel();
    }

    // -- Chrome can still force-close past preventDefault (a second Escape): stay up mid-switch
    function onClose(){
        if (pending){
            dlg.showModal();
            return;
        }
        failed = null;
        open = false;
    }

    // -- a backdrop click must start and end outside the card (not a drag off a button or a selection)
    function outside(e){
        const r = dlg.getBoundingClientRect();
        return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
    }

    function onPointerDown(e){
        down_on_backdrop = e.target === dlg && outside(e);
    }

    function onDialogClick(e){
        if (down_on_backdrop && e.target === dlg && outside(e)) cancel();
        down_on_backdrop = false;
    }

</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
<dialog
    bind:this={dlg}
    class="best-audio-modal"
    aria-labelledby="best-audio-title"
    closedby={pending ? "none" : "closerequest"}
    on:cancel={onCancel}
    on:close={onClose}
    on:pointerdown={onPointerDown}
    on:click={onDialogClick}
>
    {#if shown}
        <div class="card">

            <h2 id="best-audio-title">Switch microphone for best audio?</h2>

            {#if shown.on_headset}
                <p>Your headphones are in Bluetooth call mode because their mic is in use. That lowers the quality of everything you hear through them.</p>
            {:else}
                <p>{shown.from_label} drops to a low-quality call mode while its mic is in use.</p>
            {/if}

            <p class="question">
                <span>Switching to</span>
                <span class="device">
                    <i class="fa-thin fa-microphone"></i>
                    <span>{shown.to_label}</span>
                </span>
                <span>will fix this.</span>
            </p>

            {#if failed}
                <p class="error" role="alert">
                    {#if failed.reason === "silent"}
                        We couldn't hear anything from {failed.label}, so we switched back. If your laptop lid is closed, its microphone is turned off.
                    {:else}
                        Couldn't switch to {failed.label}. It may be in use by another app or disconnected. You can also pick a mic in Settings.
                    {/if}
                </p>
            {/if}

            <div class="actions">
                <button class="cancel" bind:this={cancel_btn} on:click={cancel} disabled={pending}>
                    {$best_audio_offer || pending ? "Cancel" : "Close"}
                </button>
                {#if $best_audio_offer || pending}
                    <button class="ok" bind:this={ok_btn} on:click={ok} disabled={pending}>
                        {pending ? "Switching…" : retry ? "Try again" : "OK"}
                    </button>
                {/if}
            </div>

        </div>
    {/if}
</dialog>

<style lang="scss">

    @import "../styles/main.scss";

    dialog.best-audio-modal {
        margin: auto; // -- the scoped reset zeroes the UA centering
        padding: 0;
        width: min(440px, calc(100vw - 32px));

        color: $light-color;
        background: $background-color;
        border: 1px solid rgba(190, 157, 134, 0.15);
        border-radius: $rounded-lg;
        box-shadow:
            0 4px 24px -1px rgba(0, 0, 0, 0.3),
            inset 0 1px 1px rgba(255, 255, 255, 0.03);

        &::backdrop {
            background: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(4px);
        }
    }

    .card {
        display: flex;
        flex-direction: column;
        gap: $space-md;
        padding: $space-lg;
        font-size: $font-sm;
        line-height: 1.45;
    }

    h2 {
        font-size: $font-lg;
        font-weight: 500;
    }

    p {
        color: rgba(250, 235, 215, 0.8);
    }

    .error {
        color: $text-secondary-color;
    }

    // -- three lines: "Switching to" / [mic] / "will fix this."
    .question {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }

    .device {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        max-width: 100%; // -- a long name ellipsizes instead of overflowing the card
        padding: 4px 8px;
        font-size: 1.2rem;
        color: $light-color;
        border-radius: $rounded-md;
        border: 1px solid rgba($copper-color, 0.45);
        background: rgba($copper-color, 0.08);

        i {
            flex: none;
            color: $copper-color;
        }

        span {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: $space-sm;

        button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 96px;
            padding: 10px 18px;
            font-family: inherit;
            font-size: $font-sm;
            border-radius: $rounded-md;
            transition: background 0.18s ease, border-color 0.18s ease;

            &:focus-visible {
                box-shadow: 0 0 0 4px rgba(139, 131, 142, 0.4);
            }
        }

        .cancel {
            color: #97877e;
            border: 1px solid rgba(190, 157, 134, 0.3);

            &:hover:not([disabled]) {
                color: #be9d86;
                border-color: rgba(190, 157, 134, 0.5);
            }
        }

        .ok {
            color: #ffd5d5;
            background: #00a48a91;
            border: 0.05em solid rgba(255, 255, 255, 0.372);

            &:hover:not([disabled]) {
                background: #00887191;
            }
        }

        button[disabled] {
            opacity: 0.6;
        }
    }

</style>
